/**
 * ИИ-подбор поиска — typed client for `/ai-search/*`.
 *
 * The user describes what they supply (ru/uz/a mix) or gives a TIN; the server
 * answers with a GROUNDED search definition — categories, ИКПУ codes, niches,
 * ru+uz keywords, regions, suggested exclusions, a budget floor — plus a live
 * lot count and up to three example lots. Nothing here invents a code: every
 * value in a proposal exists in our own reference tables, which is the whole
 * reason the proposal is built server-side rather than in a prompt on the
 * client.
 *
 * Shapes follow `ai-onboarding/CONTRACT.md` (24.09.2026) exactly. When the two
 * disagree, the contract wins and this file is the thing to change.
 *
 * ⚠️ QUOTA IS NOT A LOCAL COUNTER. Free accounts get 2 proposals for life, paid
 * ones 15 per calendar month, and ONLY a proposal carrying at least one code or
 * category spends one — a timeout, an error, an empty answer or an edit does
 * not. The server says so per response (`quota` + `consumed`), so the UI must
 * mirror what it is told rather than decrementing anything itself. Counting
 * attempts here would charge users for our own outages.
 */
import { apiRequest, ApiError } from './api';

/** Where the proposal was asked for — the server splits its metrics by this. */
export type AiSurface = 'onboarding' | 'saved_search';

/**
 * The languages a proposal can be written in — the UI locales of the two
 * deployments: ru + uz (Uzbekistan), ru + kk (Kazakhstan, where the feature
 * runs since 2026-10-15). See {@link toAiLocale}.
 */
export type AiLocale = 'ru' | 'uz' | 'kk';

/** An ИКПУ code as the proposal carries it — code plus its localized name. */
export interface AiIkpuRef {
  code: string;
  name: string;
}

/** A catalog category (slug is a valid `category` filter value). */
export interface AiCategoryRef {
  slug: string;
  name: string;
}

/** A curated niche (Spec #3), by slug. */
export interface AiNicheRef {
  slug: string;
  name: string;
}

/**
 * A region. ⚠️ Keyed by `code`, not `slug` — the contract's spelling. The
 * catalog's own `RegionItem` calls the same value `slug`, so anything crossing
 * between the two has to map it (see `regionOptions` in the review step).
 */
export interface AiRegionRef {
  code: string;
  name: string;
}

/** The server's grounded suggestion. Every field is editable by the user. */
export interface AiProposal {
  /** Short search name, written in the requested locale. */
  name: string;
  categories: AiCategoryRef[];
  /** ONLY codes that exist in our ИКПУ reference table. */
  ikpu: AiIkpuRef[];
  niches: AiNicheRef[];
  /** ru + uz spellings — the server expands both scripts, the client never does. */
  keywords: string[];
  /** Only regions the user named, or the registry's region for a TIN. */
  regions: AiRegionRef[];
  /** NOT applied — these render as unselected «+ исключить» chips. */
  suggestedExcludes: string[];
  minBudgetUzs: number | null;
  /**
   * What the free tier's "one category" rule cut from the proposal.
   *
   * Present so the UI can say «ещё N кодов — в Pro» truthfully: these codes
   * were found and then withheld, which is a different statement from "we found
   * nothing else".
   */
  locked: { ikpu: AiIkpuRef[]; niches: AiNicheRef[] };
  /**
   * Present when the server WIDENED the proposal because it matched too few
   * open lots (Kazakhstan only). The review screen says what was widened and
   * offers a dropped region / budget floor back.
   */
  widened?: AiWidening;
}

/** One widening step, in the order the server tries them. */
export type AiWidenStep = 'class' | 'category' | 'budget' | 'regions';

export interface AiWidening {
  steps: AiWidenStep[];
  /** Open lots before widening. */
  openBefore: number;
  droppedRegions: AiRegionRef[];
  droppedMinBudgetUzs: number | null;
}

/** Live lots matching the proposal now, and the 14-day average of new ones. */
export interface AiCounts {
  openNow: number;
  perDay: number;
}

/** One of the ≤3 example lots shown under the proposal. */
export interface AiExample {
  id: string;
  title: string;
  buyerName: string | null;
  deadline: string | null;
  /** So'm. Serialized as a string or a number depending on the column — both
   *  are accepted rather than assumed, since `formatBudget` strips non-digits
   *  either way. */
  budgetUzs: string | number | null;
  slug: string;
}

/**
 * Non-blocking advisories on an otherwise successful proposal.
 *
 *  - `too_broad` — over 100 new lots a day. Not an error: the search WOULD
 *    work, it would just bury the subscriber. Broad searches correlate with not
 *    paying, and the digest ceiling silently drops the overflow.
 *  - `none_now` — zero open lots today. Also not an error: a narrow search that
 *    fires twice a month is the product working, as long as we say so.
 */
export type AiWarning = 'too_broad' | 'none_now';

/** Quota as the SERVER sees it. `used`/`limit` are per `scope`. */
export interface AiQuota {
  used: number;
  limit: number;
  scope: 'lifetime' | 'month';
}

export interface AiProposeResponse {
  draftId: string;
  proposal: AiProposal;
  counts: AiCounts;
  examples: AiExample[];
  warnings: AiWarning[];
  quota: AiQuota;
  /** Whether THIS response spent a quota unit. Never inferred client-side. */
  consumed: boolean;
  /** The input was over {@link AI_MAX_TEXT} and the tail was dropped. */
  truncated?: boolean;
}

export interface AiProposeInput {
  /** Free text, ru/uz/mixed. At least one of `text`/`tin` is required. */
  text?: string;
  /** Company TIN — the digit count is the country's (`countryConfig`). */
  tin?: string;
  locale: AiLocale;
  surface: AiSurface;
}

/** What became of a draft. `edited_saved` = saved after the user changed it. */
export type AiOutcome = 'saved' | 'edited_saved' | 'manual' | 'abandoned';

export interface AiOutcomeInput {
  outcome: AiOutcome;
  savedSearchId?: string;
  /** The payload as it went to the saved-search create/patch. */
  final?: Record<string, unknown>;
  /** How many edits the user made to the proposal before saving. */
  editsCount?: number;
}

/**
 * Hard input cap, mirrored from the server so the textarea can stop at the same
 * number instead of sending a tail that will be discarded (and never letting a
 * paste of a whole tender spec reach the model).
 */
export const AI_MAX_TEXT = 600;

/**
 * The error codes this feature owns.
 *
 * Every one of them has its own screen (mockup 5), so they are a closed set
 * here rather than free-form strings: an unrecognised code must fall through to
 * the generic «ИИ не ответил» card, not render a raw server message.
 */
export const AI_ERROR_CODES = [
  'ai_quota_exhausted',
  'ai_not_understood',
  'ai_timeout',
  'ai_error',
  'ai_input_empty',
  // Kazakhstan only: a БИН alone that we know nothing about. Not a failure
  // screen — the onboarding step asks for a description instead.
  'ai_tin_unknown',
] as const;
export type AiErrorCode = (typeof AI_ERROR_CODES)[number];

/**
 * The feature's error code carried by a thrown error, or null.
 *
 * Null covers three genuinely different things — a NetworkError, an ApiError
 * with some other code, an unexpected throw — and they share one treatment:
 * the «ИИ не ответил» card with a retry, because none of them is the user's
 * fault and none of them spends quota.
 */
export function aiErrorCode(e: unknown): AiErrorCode | null {
  if (!(e instanceof ApiError) || !e.code) return null;
  return (AI_ERROR_CODES as readonly string[]).includes(e.code) ? (e.code as AiErrorCode) : null;
}

/**
 * The quota the server attached to a refusal, when it did.
 *
 * ⚠️ Only reachable when the API nests `quota` INSIDE the error item — that is
 * all `ApiError.details` carries (`api.ts:handleResponse` keeps `errors[0]`).
 * A `quota` sitting at the top level of the error body is invisible here, which
 * is why every caller treats this as optional and falls back to the last quota
 * it was told rather than to a guess.
 */
export function aiErrorQuota(e: unknown): AiQuota | null {
  if (!(e instanceof ApiError)) return null;
  const q = e.details?.quota as Partial<AiQuota> | undefined;
  if (!q || typeof q.used !== 'number' || typeof q.limit !== 'number') return null;
  return { used: q.used, limit: q.limit, scope: q.scope === 'month' ? 'month' : 'lifetime' };
}

/** Proposals left, never negative — `limit - used`, clamped. */
export function quotaLeft(q: AiQuota | null | undefined): number {
  if (!q) return 0;
  return Math.max(0, q.limit - q.used);
}

/**
 * The UI locale as the API accepts it: `uz` and `kk` pass through (a Kazakh
 * reader gets the search name in Kazakh), anything else is Russian.
 */
export function toAiLocale(locale: string): AiLocale {
  return locale === 'uz' || locale === 'kk' ? locale : 'ru';
}

/**
 * POST /ai-search/propose — ask for a grounded search definition.
 *
 * Authenticated only (there is no guest path in v1). The text is trimmed and
 * capped here as well as server-side: the cap is a cost control, and the
 * cheapest place to enforce it is before the request leaves.
 */
export async function propose(input: AiProposeInput): Promise<AiProposeResponse> {
  const text = input.text?.trim().slice(0, AI_MAX_TEXT);
  return apiRequest<AiProposeResponse>('/ai-search/propose', {
    method: 'POST',
    body: JSON.stringify({
      ...(text ? { text } : {}),
      ...(input.tin ? { tin: input.tin } : {}),
      locale: input.locale,
      surface: input.surface,
    }),
  });
}

/**
 * POST /ai-search/drafts/:id/outcome — what happened to a proposal (204).
 *
 * Fire-and-forget by design: this is telemetry for the harness, and a failed
 * report must never surface to a user who has just saved a search successfully.
 * The create endpoint sets `saved`/`edited_saved` itself when it receives an
 * `aiDraftId`, so this call is for the paths create does not see — an abandoned
 * sheet, a switch to the manual picker, and a PATCH-based save.
 */
export async function reportOutcome(draftId: string, input: AiOutcomeInput): Promise<void> {
  await apiRequest<void>(`/ai-search/drafts/${encodeURIComponent(draftId)}/outcome`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Same as {@link reportOutcome}, but swallows every failure. */
export function reportOutcomeQuietly(draftId: string, input: AiOutcomeInput): void {
  void reportOutcome(draftId, input).catch(() => {});
}

/** GET /ai-search/quota — how many proposals are left, BEFORE pressing. */
export async function getQuota(): Promise<AiQuota> {
  return apiRequest<AiQuota>('/ai-search/quota');
}
